import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';


export const TESTING_ENVIRONMENTS = ['dev', 'staging'];
export const PROD_ENVIRONMENTS = ['prod', 'deprod', 'krprod', 'jpprod'];
export const ALL_ENVIRONMENTS = [...TESTING_ENVIRONMENTS, ...PROD_ENVIRONMENTS];

/**
 * Parses the branch name from a Backstage location annotation.
 * Backstage uses /tree/ for managed-by-location and source-location,
 * and /blob/ for managed-by-origin-location and edit-url.
 * Examples:
 *   "url:https://github.com/rescale/rescale-ai-agents/tree/backstage-my_agent/src/..."
 *   "url:https://github.com/rescale/rescale-ai-agents/blob/main/src/..."
 */
function parseBranchFromLocation(location: string | undefined): string | null {
  if (!location) return null;
  const match = location.match(/\/(?:blob|tree)\/([^/]+)\//);
  return match ? match[1] : null;
}

/**
 * Looks up the agent entity's branch from catalog annotations and determines
 * whether it is a feature branch (anything other than the main branch).
 *
 * Returns:
 * - `branch`: the parsed branch name, or null if not resolved
 * - `isFeatureBranch`: true when the agent is registered from a non-main branch
 * - `loading`: true while the catalog lookup is in flight
 */
export function useAgentBranch(agentRef: string | undefined) {
  const catalogApi = useApi(catalogApiRef);
  const [branch, setBranch] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!agentRef) {
      setBranch(null);
      return;
    }

    setLoading(true);
    catalogApi
      .getEntityByRef(agentRef)
      .then(entity => {
        if (!entity) {
          setBranch(null);
          return;
        }
        const annotations = entity.metadata?.annotations ?? {};
        const managedBy = annotations['backstage.io/managed-by-location'];
        const sourceLocation = annotations['backstage.io/source-location'];
        const parsed =
          parseBranchFromLocation(managedBy) ??
          parseBranchFromLocation(sourceLocation);
        setBranch(parsed);
      })
      .catch(() => {
        setBranch(null);
      })
      .finally(() => setLoading(false));
  }, [agentRef, catalogApi]);

  // TODO: change back to main after testing
  const isFeatureBranch = !!branch && branch !== 'main';

  return { branch, isFeatureBranch, loading };
}
